export interface AdvertorialSection {
  type:
    | 'paragraph'
    | 'subheading'
    | 'image'
    | 'callout'
    | 'cta'
    | 'stats'
    | 'quote'
  text?: string
  src?: string
  caption?: string
  author?: string
  items?: { value: string; label: string }[]
}

export interface AdvertorialContent {
  serviceSlug: string
  serviceName: string
  headline: string
  subtitle: string
  heroImage: string
  sections: AdvertorialSection[]
}

export const advertorials: AdvertorialContent[] = [
  {
    serviceSlug: 'window-replacement',
    serviceName: 'Window Replacement',
    headline:
      'Homeowners Are Rushing To Replace Windows While Savings Programs Last',
    subtitle:
      'Energy Costs Are Rising: New Incentives Are Making Window Replacement More Affordable Than Ever',
    heroImage:
      'https://cdn.magicpatterns.com/uploads/vHoCxtteVitWWLqvuvwzwq/ChatGPT_Image_Apr_20,_2026,_08_51_28_AM.png',
    sections: [
      {
        type: 'paragraph',
        text: "If you've been putting off replacing your old, drafty windows, now might be the best time to act. A combination of federal tax credits, manufacturer rebates, and competitive pricing has created a perfect storm of savings for homeowners across the country.",
      },
      {
        type: 'stats',
        items: [
          { value: '30%', label: 'Federal Tax Credit Available' },
          { value: '$12,000', label: 'Max Credit Per Household' },
          { value: '25-40%', label: 'Energy Savings Per Year' },
        ],
      },
      {
        type: 'subheading',
        text: 'Why Are So Many Homeowners Replacing Windows Right Now?',
      },
      {
        type: 'paragraph',
        text: 'The Inflation Reduction Act of 2022 expanded energy efficiency tax credits, allowing homeowners to claim up to $600 per year for qualifying window replacements. Combined with state-level incentives and utility rebates, the effective cost of new windows has dropped significantly.',
      },
      {
        type: 'paragraph',
        text: "But these programs won't last forever. Many incentives are set to phase down or expire in the coming years, creating urgency among homeowners who want to lock in the best deals.",
      },
      { type: 'cta' },
      { type: 'subheading', text: 'The Hidden Cost of Old Windows' },
      {
        type: 'paragraph',
        text: "Most homeowners don't realize how much money they're losing through inefficient windows. According to the Department of Energy, heat gain and loss through windows accounts for 25-30% of residential heating and cooling energy use. That translates to hundreds of dollars per year in wasted energy.",
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Modern energy-efficient windows can dramatically reduce heating and cooling costs.',
      },
      {
        type: 'paragraph',
        text: 'Beyond energy costs, old windows can lead to moisture damage, mold growth, and decreased home value. Single-pane windows and those with broken seals are particularly problematic, allowing condensation to form between panes and reducing visibility.',
      },
      {
        type: 'quote',
        text: 'We replaced all 14 windows in our home and our energy bill dropped by $180 per month. The windows paid for themselves in under 3 years.',
        author: 'Sarah M., Phoenix, AZ',
      },
      { type: 'subheading', text: 'What to Look for in Replacement Windows' },
      {
        type: 'paragraph',
        text: 'When shopping for replacement windows, there are several key factors to consider. Energy Star certification is a must — these windows meet strict energy efficiency guidelines set by the EPA. Look for low-E glass coatings, argon or krypton gas fills between panes, and quality frame materials like vinyl or fiberglass.',
      },
      {
        type: 'paragraph',
        text: 'Professional installation is equally important. Even the best windows will underperform if installed incorrectly. Look for companies that offer comprehensive warranties covering both the product and the installation.',
      },
      {
        type: 'callout',
        text: '💡 Pro Tip: Get at least 3 quotes from different window companies before making a decision. Prices can vary by 30-50% for the same quality product.',
      },
      { type: 'cta' },
      { type: 'subheading', text: 'How to Get Started' },
      {
        type: 'paragraph',
        text: 'The first step is getting a free, no-obligation quote from qualified window replacement professionals in your area. Most reputable companies will send a consultant to your home to assess your current windows, take measurements, and provide a detailed estimate.',
      },
      {
        type: 'paragraph',
        text: "With current incentives and competitive pricing, there's never been a better time to invest in new windows for your home. Don't wait until these programs expire — start comparing quotes today and see how much you could save.",
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'roofing',
    serviceName: 'Roofing',
    headline:
      'Why Smart Homeowners Are Replacing Their Roofs Before Insurance Rates Climb Higher',
    subtitle:
      'New Roofing Materials and Installation Techniques Are Saving Homeowners Thousands on Insurance Premiums',
    heroImage:
      'https://images.unsplash.com/photo-1632759145351-1d592919f522?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "Home insurance premiums have skyrocketed in recent years, with some homeowners seeing increases of 30-50%. What many don't realize is that a new roof can significantly reduce those premiums — sometimes by enough to offset a large portion of the replacement cost.",
      },
      {
        type: 'stats',
        items: [
          { value: '20-35%', label: 'Insurance Discount with New Roof' },
          { value: '25+ Years', label: 'Modern Roof Lifespan' },
          { value: '$8,000-15,000', label: 'Average Replacement Cost' },
        ],
      },
      {
        type: 'subheading',
        text: 'The Insurance Connection Most Homeowners Miss',
      },
      {
        type: 'paragraph',
        text: 'Insurance companies assess risk based on the age and condition of your roof. A roof over 15 years old is considered high-risk, leading to higher premiums or even policy non-renewal. By investing in a new roof with impact-resistant shingles, you can qualify for substantial insurance discounts.',
      },
      {
        type: 'paragraph',
        text: 'In states prone to severe weather — Florida, Texas, Colorado, and others — these discounts can be even more significant. Some insurers offer up to 35% off premiums for roofs that meet specific wind and impact resistance ratings.',
      },
      { type: 'cta' },
      {
        type: 'subheading',
        text: 'Modern Roofing Technology Has Changed the Game',
      },
      {
        type: 'paragraph',
        text: "Today's roofing materials are light-years ahead of what was available even a decade ago. Architectural shingles, metal roofing, and synthetic slate options offer superior durability, energy efficiency, and curb appeal compared to traditional 3-tab shingles.",
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1558618666-fcd25c85f82e?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Modern architectural shingles offer superior protection and dramatically improve curb appeal.',
      },
      {
        type: 'paragraph',
        text: 'Cool roof technology, which reflects more sunlight and absorbs less heat, can reduce cooling costs by 10-15%. When combined with proper attic ventilation and insulation, a new roof becomes a comprehensive energy efficiency upgrade.',
      },
      {
        type: 'quote',
        text: 'After replacing our 20-year-old roof, our insurance premium dropped by $1,400 per year. The roof essentially pays for itself over time.',
        author: 'Mike T., Dallas, TX',
      },
      {
        type: 'callout',
        text: "⚠️ Important: Many roofing companies offer free inspections. If your roof is over 10 years old, it's worth getting a professional assessment to understand its current condition.",
      },
      { type: 'cta' },
      { type: 'subheading', text: 'Financing Makes It Easier Than Ever' },
      {
        type: 'paragraph',
        text: 'Most top roofing companies now offer flexible financing options, including 0% APR for qualified buyers. This means you can start saving on insurance and energy costs immediately while spreading the investment over time.',
      },
      {
        type: 'paragraph',
        text: "Don't wait for a leak or storm damage to force your hand. Proactive roof replacement gives you the power to choose the best materials, the best contractor, and the best financing — on your timeline.",
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'solar',
    serviceName: 'Solar Panels',
    headline:
      'Homeowners Are Rushing To Install Solar While Savings Programs Last',
    subtitle:
      'Prices Are Rising: New Market Shifts Are Ending Current Solar Benefits for Homeowners',
    heroImage:
      'https://images.unsplash.com/photo-1509391366360-2e959784a276?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'The solar industry is at a tipping point. Federal tax credits, falling panel prices, and rising electricity rates have created an unprecedented opportunity for homeowners. But market conditions are shifting, and the window of maximum savings is closing.',
      },
      {
        type: 'stats',
        items: [
          { value: '30%', label: 'Federal Tax Credit (ITC)' },
          { value: '$20,000-35,000', label: 'Average 25-Year Savings' },
          { value: '90%', label: 'Bill Reduction Possible' },
        ],
      },
      { type: 'subheading', text: 'Why 2026 Is the Year to Go Solar' },
      {
        type: 'paragraph',
        text: "The federal Investment Tax Credit (ITC) currently offers a 30% tax credit on the total cost of a solar installation. This credit is scheduled to step down to 26% in 2033 and 22% in 2034 before expiring for residential installations. For a typical $25,000 system, that's a difference of $1,000-2,000 in savings.",
      },
      {
        type: 'paragraph',
        text: 'Meanwhile, electricity rates continue to climb. The average American household saw a 15% increase in electricity costs over the past two years, and utilities are requesting further rate hikes. Solar locks in your energy costs for 25+ years.',
      },
      { type: 'cta' },
      { type: 'subheading', text: 'The Real Numbers Behind Solar Savings' },
      {
        type: 'paragraph',
        text: "Let's break down a typical scenario. A homeowner paying $200/month for electricity installs a $25,000 solar system. After the 30% tax credit, the net cost is $17,500. With financing, the monthly payment is often less than the current electric bill — meaning you start saving from day one.",
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1508514177221-188b1cf16e9d?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Solar panels can reduce or eliminate your monthly electricity bill while increasing home value.',
      },
      {
        type: 'paragraph',
        text: "Over 25 years, the average solar homeowner saves $20,000-35,000 compared to buying electricity from the grid. And that's using conservative estimates — if electricity rates rise faster than expected, savings could be even higher.",
      },
      {
        type: 'quote',
        text: 'Our electric bill went from $201 to $16.91 after installing solar. I only wish we had done it sooner.',
        author: 'Jennifer L., Scottsdale, AZ',
      },
      {
        type: 'callout',
        text: "☀️ Did You Know? Solar panels increase home value by an average of 4.1%, according to Zillow. That's an additional $10,000-20,000 in equity for the average home.",
      },
      { type: 'cta' },
      { type: 'subheading', text: 'Getting Started Is Easier Than You Think' },
      {
        type: 'paragraph',
        text: 'The first step is a free solar assessment. A qualified installer will evaluate your roof, analyze your energy usage, and design a custom system. Most homeowners are surprised to learn they qualify for $0-down financing with payments lower than their current electric bill.',
      },
      {
        type: 'paragraph',
        text: "Don't wait for incentives to decrease or electricity rates to climb higher. Get your free solar quote today and see exactly how much you could save.",
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'walk-in-showers',
    serviceName: 'Walk-In Showers',
    headline:
      'Why Thousands of Homeowners Are Switching to Walk-In Showers This Year',
    subtitle:
      'Safety, Accessibility, and Modern Design Are Driving a Bathroom Revolution',
    heroImage:
      'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "The traditional bathtub is quickly becoming a relic of the past. Homeowners across the country are discovering that walk-in showers offer superior safety, accessibility, and style — all while increasing their home's value.",
      },
      {
        type: 'stats',
        items: [
          { value: '80%', label: 'Of Falls Happen in Bathrooms' },
          { value: '$5,000-15,000', label: 'Average Installation Cost' },
          { value: '1-2 Days', label: 'Typical Installation Time' },
        ],
      },
      { type: 'subheading', text: "The Safety Factor You Can't Ignore" },
      {
        type: 'paragraph',
        text: 'According to the CDC, over 230,000 Americans visit emergency rooms each year due to bathroom injuries. The majority of these involve slipping while getting in or out of a traditional bathtub. Walk-in showers with low or zero-threshold entries dramatically reduce this risk.',
      },
      { type: 'cta' },
      { type: 'subheading', text: 'Modern Walk-In Showers Are Stunning' },
      {
        type: 'paragraph',
        text: "Gone are the days when accessible bathrooms meant institutional-looking fixtures. Today's walk-in showers feature frameless glass enclosures, rainfall showerheads, built-in benches, and luxury tile work that rivals high-end spa designs.",
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1620626011761-996317b8d101?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Modern walk-in showers combine safety features with spa-like luxury design.',
      },
      {
        type: 'quote',
        text: 'We converted our old tub to a walk-in shower and it completely transformed our bathroom. It feels like a spa now, and we feel so much safer.',
        author: 'Patricia W., Orlando, FL',
      },
      {
        type: 'callout',
        text: '💡 Pro Tip: Many walk-in shower installations can be completed in just 1-2 days with minimal disruption to your daily routine.',
      },
      { type: 'cta' },
      { type: 'subheading', text: 'Get Your Free Consultation' },
      {
        type: 'paragraph',
        text: 'The best way to explore your options is with a free in-home consultation. A design specialist will assess your bathroom, discuss your needs and preferences, and provide a detailed quote — all with no obligation.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'walk-in-tubs',
    serviceName: 'Walk-In Tubs',
    headline:
      'The Walk-In Tub Revolution: Why Seniors Are Making the Switch in Record Numbers',
    subtitle:
      'New Hydrotherapy Features and Safety Innovations Are Changing How Americans Bathe',
    heroImage:
      'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'For millions of aging Americans, the simple act of bathing has become a source of anxiety. Traditional bathtubs require stepping over high walls, creating a fall risk that increases with age. Walk-in tubs are solving this problem while adding therapeutic benefits that improve quality of life.',
      },
      {
        type: 'stats',
        items: [
          { value: '3 inches', label: 'Low Step-In Threshold' },
          { value: '15+ Jets', label: 'Hydrotherapy Options' },
          { value: '$0 Down', label: 'Financing Available' },
        ],
      },
      { type: 'cta' },
      { type: 'subheading', text: "More Than Just Safety — It's Therapy" },
      {
        type: 'image',
        src: 'https://cdn.magicpatterns.com/uploads/uztf2XVa5Quyg5Kf3Z99xf/Contractor_installing_walk-in_tub_in_bathroom.png',
        caption:
          'Professional installation ensures your new walk-in tub is perfectly fitted and water-tight.',
      },
      {
        type: 'paragraph',
        text: "Modern walk-in tubs offer hydrotherapy jets, heated surfaces, chromotherapy lighting, and aromatherapy systems. These features aren't just luxurious — they provide real therapeutic benefits for arthritis, joint pain, circulation issues, and stress relief.",
      },
      {
        type: 'quote',
        text: 'My walk-in tub has been life-changing. The hydrotherapy jets help with my arthritis pain, and I no longer worry about falling.',
        author: 'Robert H., Nashville, TN',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: "Don't compromise on safety or comfort. Get a free quote today and discover how a walk-in tub can transform your daily routine.",
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'stairlifts',
    serviceName: 'Stairlifts',
    headline:
      'How Stairlifts Are Helping Homeowners Stay in the Homes They Love',
    subtitle:
      'Aging in Place Is Now More Affordable and Accessible Than Ever Before',
    heroImage:
      'https://images.unsplash.com/photo-1582719478250-c89cae4dc85b?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "The desire to age in place is universal. Nobody wants to leave the home they've lived in for decades just because stairs have become difficult. Stairlifts offer a simple, affordable solution that preserves independence and keeps families together.",
      },
      {
        type: 'stats',
        items: [
          { value: '90%', label: 'Want to Age in Place' },
          { value: '$2,000-5,000', label: 'Straight Stairlift Cost' },
          { value: '2-4 Hours', label: 'Installation Time' },
        ],
      },
      { type: 'cta' },
      { type: 'subheading', text: 'Modern Stairlifts Are Sleek and Reliable' },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1572120360610-d971b9d7767c?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Modern stairlifts feature slim profiles that fold away when not in use.',
      },
      {
        type: 'paragraph',
        text: "Today's stairlifts are nothing like the bulky, noisy models of the past. Modern units feature slim profiles that fold flat against the wall, whisper-quiet motors, and battery backup systems that work even during power outages.",
      },
      {
        type: 'quote',
        text: "The stairlift gave my mother her independence back. She can move freely between floors without anyone's help.",
        author: 'David K., Chicago, IL',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free in-home assessment today. A specialist will evaluate your staircase and provide a custom quote with no obligation.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'gutter-guards',
    serviceName: 'Gutter Guards',
    headline:
      'The End of Gutter Cleaning: Why Homeowners Are Installing Gutter Guards in Droves',
    subtitle:
      'New Micro-Mesh Technology Eliminates Clogs and Protects Your Home Year-Round',
    heroImage:
      'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'Every year, thousands of homeowners are injured falling from ladders while cleaning gutters. And clogged gutters cause billions of dollars in water damage to foundations, basements, and landscaping. Gutter guards eliminate both problems permanently.',
      },
      {
        type: 'stats',
        items: [
          { value: '500,000+', label: 'Ladder Injuries Per Year' },
          { value: '$10,000+', label: 'Average Water Damage Cost' },
          { value: 'Lifetime', label: 'Warranty Available' },
        ],
      },
      { type: 'cta' },
      { type: 'subheading', text: 'How Micro-Mesh Technology Works' },
      {
        type: 'image',
        src: 'https://cdn.magicpatterns.com/uploads/nHEsPtECKST1sy11QfZeZo/Roof_worker_installing_gutter_guard.png',
        caption:
          'Micro-mesh gutter guards keep debris out while allowing water to flow freely.',
      },
      {
        type: 'paragraph',
        text: 'The latest gutter guards use surgical-grade stainless steel micro-mesh that allows water to flow through while blocking leaves, pine needles, and even roof grit. This technology handles up to 32 inches of rain per hour — far more than any natural rainfall.',
      },
      {
        type: 'quote',
        text: 'I spent $300 every year on gutter cleaning. My gutter guards paid for themselves in just a few years, and I never have to climb a ladder again.',
        author: 'Tom R., Portland, OR',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free gutter inspection and quote today. Most installations are completed in just one day.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'siding',
    serviceName: 'Siding',
    headline:
      "Why New Siding Is the Best Home Investment You're Not Thinking About",
    subtitle:
      'Modern Siding Options Slash Energy Bills, Eliminate Maintenance, and Transform Curb Appeal',
    heroImage:
      'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'When homeowners think about major home improvements, siding rarely tops the list. But new siding consistently delivers one of the highest returns on investment of any home improvement project — often recouping 70-80% of its cost at resale.',
      },
      {
        type: 'image',
        src: 'https://cdn.magicpatterns.com/uploads/e3vgaCMRMJVVB4ikxPQXtA/House_siding_installation_in_progress.png',
        caption:
          "New siding installation can dramatically improve your home's energy efficiency and curb appeal.",
      },
      {
        type: 'stats',
        items: [
          { value: '75%', label: 'Average ROI at Resale' },
          { value: '20%', label: 'Energy Savings Possible' },
          { value: '50+ Years', label: 'Modern Siding Lifespan' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Insulated vinyl and fiber cement siding options have revolutionized the industry. These materials never need painting, resist rot and insects, and provide an additional layer of insulation that reduces energy costs year-round.',
      },
      {
        type: 'quote',
        text: "Our new siding completely transformed our home's appearance. Neighbors thought we built a new house!",
        author: 'Karen S., Minneapolis, MN',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free consultation and see how new siding can transform your home. Most companies offer flexible financing with low monthly payments.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'bathroom-remodel',
    serviceName: 'Bathroom Remodel',
    headline: 'The Bathroom Remodel Boom: Why Homeowners Are Investing Now',
    subtitle:
      'Quick-Install Solutions Mean You Can Have a New Bathroom in Days, Not Weeks',
    heroImage:
      'https://images.unsplash.com/photo-1552321554-5fefe8c9ef14?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'Bathroom remodels have long been one of the most popular home improvement projects, and for good reason. A well-executed bathroom renovation can recoup 60-70% of its cost at resale while dramatically improving your daily quality of life.',
      },
      {
        type: 'image',
        src: 'https://cdn.magicpatterns.com/uploads/7LG8d1xsN1EpGcbXWXtdPd/Bathroom_remodel_in_progress__.png',
        caption:
          'Modern bathroom remodels can be completed in a fraction of the time of traditional renovations.',
      },
      {
        type: 'stats',
        items: [
          { value: '60-70%', label: 'ROI at Resale' },
          { value: '1-2 Days', label: 'Quick-Install Options' },
          { value: '$10,000-25,000', label: 'Average Remodel Cost' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'New quick-install bathroom solutions have made remodeling faster and more affordable than ever. Companies now offer complete tub-to-shower conversions, custom surrounds, and full bathroom makeovers that can be completed in as little as one day.',
      },
      {
        type: 'quote',
        text: "I couldn't believe they transformed our entire bathroom in just two days. It looks like it belongs in a luxury hotel.",
        author: 'Linda M., Atlanta, GA',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Start with a free design consultation. A specialist will help you explore options that fit your style, needs, and budget.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'kitchen-remodel',
    serviceName: 'Kitchen Remodel',
    headline:
      'Kitchen Remodeling Secrets: How to Get a Dream Kitchen Without Breaking the Bank',
    subtitle:
      'Cabinet Refacing and Smart Upgrades Are Saving Homeowners 50% on Kitchen Renovations',
    heroImage:
      'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'A full kitchen remodel can easily cost $50,000 or more. But savvy homeowners are discovering that cabinet refacing, countertop upgrades, and strategic improvements can deliver a stunning transformation at a fraction of the cost.',
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1556911220-e15b29be8c8f?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Cabinet refacing and smart upgrades can transform your kitchen for half the cost of a full remodel.',
      },
      {
        type: 'stats',
        items: [
          { value: '50%', label: 'Savings vs. Full Remodel' },
          { value: '75%', label: 'ROI at Resale' },
          { value: '3-5 Days', label: 'Refacing Timeline' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Cabinet refacing replaces just the doors and drawer fronts while keeping the existing cabinet boxes. Combined with new hardware, countertops, and a fresh backsplash, the result is a kitchen that looks completely new.',
      },
      {
        type: 'quote',
        text: 'We saved over $20,000 by refacing instead of replacing our cabinets. The kitchen looks brand new and it only took 4 days.',
        author: 'James & Maria C., San Diego, CA',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free kitchen design consultation today. See how much you could save with smart remodeling strategies.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'flooring',
    serviceName: 'Flooring',
    headline: "The Flooring Upgrade That's Transforming Homes Across America",
    subtitle:
      'Luxury Vinyl and Modern Hardwood Options Offer Beauty, Durability, and Easy Installation',
    heroImage:
      'https://images.unsplash.com/photo-1581858726788-75bc0f6a952d?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "New flooring is one of the fastest ways to transform the look and feel of your entire home. And with today's luxury vinyl plank (LVP) and engineered hardwood options, you can get the look of premium materials at a fraction of the cost.",
      },
      {
        type: 'image',
        src: 'https://cdn.magicpatterns.com/uploads/bBvVy9ZngdjBVioxvhvvbv/Home_renovation__installing_wood_flooring.png',
        caption:
          'Luxury vinyl plank offers the look of real hardwood with superior durability and water resistance.',
      },
      {
        type: 'stats',
        items: [
          { value: '70-80%', label: 'ROI at Resale' },
          { value: 'Next Day', label: 'Installation Available' },
          { value: '25+ Years', label: 'LVP Lifespan' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: "Luxury vinyl plank has become the fastest-growing flooring category for good reason. It's waterproof, scratch-resistant, comfortable underfoot, and virtually indistinguishable from real hardwood. Plus, it can be installed over existing floors, saving time and money.",
      },
      {
        type: 'quote',
        text: 'We replaced the carpet in our entire first floor with LVP. It looks like real hardwood and has survived two kids and a dog without a scratch.',
        author: 'Amy T., Charlotte, NC',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free in-home flooring consultation. Browse hundreds of styles and get a quote — all from the comfort of your home.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'hvac',
    serviceName: 'HVAC',
    headline: 'Your Old HVAC System Is Costing You More Than You Think',
    subtitle:
      'New High-Efficiency Systems Cut Energy Bills by 30-50% While Improving Home Comfort',
    heroImage:
      'https://images.unsplash.com/photo-1585771724684-38269d6639fd?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "If your HVAC system is more than 10 years old, you're likely paying 30-50% more than necessary to heat and cool your home. Modern high-efficiency systems have made dramatic improvements in energy performance, and generous rebates make upgrading more affordable than ever.",
      },
      {
        type: 'image',
        src: 'https://cdn.magicpatterns.com/uploads/4LVZUr8iS1pjfVRpjj65dM/HVAC_technician_servicing_outdoor_unit.png',
        caption:
          'A modern, high-efficiency HVAC system can significantly reduce your monthly energy bills.',
      },
      {
        type: 'stats',
        items: [
          { value: '30-50%', label: 'Energy Savings' },
          { value: '$7,500', label: 'Available Tax Credits' },
          { value: '15-20 Years', label: 'New System Lifespan' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'The Inflation Reduction Act provides up to $2,000 in tax credits for qualifying heat pumps and $600 for central air conditioners. Combined with manufacturer rebates and utility incentives, the effective cost of a new system is significantly reduced.',
      },
      {
        type: 'quote',
        text: 'Our new heat pump cut our heating bill in half and keeps the house more comfortable than our old furnace ever did.',
        author: 'Steve & Nancy B., Richmond, VA',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free HVAC assessment today. A certified technician will evaluate your current system and provide options that fit your home and budget.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'painting',
    serviceName: 'Painting',
    headline:
      'The Fastest Way to Transform Your Home: Professional Painting Delivers Instant Results',
    subtitle:
      "Expert Painters Deliver Flawless Results in Days — Here's What You Need to Know",
    heroImage:
      'https://images.unsplash.com/photo-1562259949-e8e7689d7828?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'Few home improvements deliver as much impact per dollar as professional painting. A fresh coat of paint can make a dated home look modern, increase curb appeal, and boost resale value — all in just a few days.',
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1589939705384-5185137a7f0f?auto=format&fit=crop&w=1200&q=80',
        caption:
          "Professional painters deliver flawless results and can transform your home's interior in just a few days.",
      },
      {
        type: 'stats',
        items: [
          { value: '100%+', label: 'ROI for Exterior Paint' },
          { value: '2-4 Days', label: 'Average Interior Project' },
          { value: '7-10 Years', label: 'Quality Paint Lifespan' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: "Professional painters deliver results that DIY simply can't match. From proper surface preparation to precise cutting-in and even coverage, the difference between professional and amateur work is immediately visible.",
      },
      {
        type: 'quote',
        text: 'We tried painting our living room ourselves and it was a disaster. The professional crew did our entire first floor in two days and it looks perfect.',
        author: 'Rachel G., Denver, CO',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free painting estimate today. Most companies offer free color consultations to help you choose the perfect palette.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'plumbing',
    serviceName: 'Plumbing',
    headline:
      "Hidden Plumbing Problems Could Be Costing You Thousands — Here's How to Fix Them",
    subtitle:
      'From Slow Drains to Hidden Leaks, Professional Plumbers Save Homeowners From Costly Damage',
    heroImage:
      'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'A small plumbing leak can waste up to 10,000 gallons of water per year and cause thousands in structural damage. Yet many homeowners ignore warning signs until a minor issue becomes a major emergency.',
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1585704032915-c3400ca199e7?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Professional plumbers use advanced technology to detect hidden leaks before they cause major damage.',
      },
      {
        type: 'stats',
        items: [
          { value: '10,000 gal', label: 'Wasted Per Year by Leaks' },
          { value: '$3,000+', label: 'Average Water Damage Cost' },
          { value: '24/7', label: 'Emergency Service Available' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Professional plumbers use advanced camera inspection technology to identify hidden problems before they cause damage. Regular maintenance and prompt repairs can save thousands in emergency costs.',
      },
      {
        type: 'quote',
        text: 'A routine inspection found a hidden leak under our slab that could have caused $15,000 in damage. The repair cost us less than $500.',
        author: 'Carlos M., Houston, TX',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: "Don't wait for an emergency. Get a free plumbing inspection today and protect your home from costly water damage.",
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'electrical',
    serviceName: 'Electrical',
    headline: "Is Your Home's Electrical System a Hidden Safety Hazard?",
    subtitle:
      'Outdated Wiring and Overloaded Panels Are Putting Thousands of Homes at Risk',
    heroImage:
      'https://images.unsplash.com/photo-1621905251189-08b45d6a269e?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'Electrical fires cause an estimated $1.3 billion in property damage each year. Many of these fires start in homes with outdated wiring, overloaded panels, or faulty connections that homeowners never knew existed.',
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Upgrading your electrical panel ensures your home can safely handle modern power demands.',
      },
      {
        type: 'stats',
        items: [
          { value: '51,000', label: 'Electrical Fires Per Year' },
          { value: '$1.3B', label: 'Annual Property Damage' },
          { value: '40+ Years', label: 'Age of At-Risk Wiring' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: "If your home was built before 1980, there's a good chance it has aluminum wiring, undersized panels, or other electrical issues that pose safety risks. A professional electrical inspection can identify these hazards before they cause problems.",
      },
      {
        type: 'quote',
        text: 'Our electrician found our panel was double-tapped and overloaded. The upgrade cost $2,500 but potentially saved our home from a fire.',
        author: 'Brian & Lisa P., Columbus, OH',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Schedule a free home safety inspection today. A licensed electrician will assess your system and provide recommendations.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'fencing',
    serviceName: 'Fencing',
    headline:
      "The Smart Homeowner's Guide to Fencing: Privacy, Security, and Value",
    subtitle:
      'New Materials and Installation Methods Make Quality Fencing More Affordable Than Ever',
    heroImage:
      'https://images.unsplash.com/photo-1578662996442-48f60103fc96?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "A quality fence does more than mark your property line. It provides privacy, security, noise reduction, and can increase your home's value by 5-12%. With new materials like composite and vinyl, today's fences require virtually zero maintenance.",
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1582268611958-ebfd161ef9cf?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Modern vinyl and composite fencing materials require virtually zero maintenance.',
      },
      {
        type: 'stats',
        items: [
          { value: '5-12%', label: 'Home Value Increase' },
          { value: '50+ Years', label: 'Vinyl Fence Lifespan' },
          { value: '$0', label: 'Maintenance Cost (Vinyl)' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: "Vinyl and composite fencing have revolutionized the industry. These materials won't rot, warp, or need painting — ever. They maintain their appearance for decades with nothing more than an occasional rinse with a garden hose.",
      },
      {
        type: 'quote',
        text: "We installed a vinyl privacy fence 8 years ago and it still looks brand new. Best home investment we've made.",
        author: 'Mark & Susan D., Austin, TX',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free fence estimate today. Most companies offer 3D visualization tools so you can see exactly how your new fence will look.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'insulation',
    serviceName: 'Insulation',
    headline:
      'The Invisible Upgrade That Pays for Itself: Why Homeowners Are Investing in Insulation',
    subtitle:
      'Proper Insulation Cuts Energy Bills by 20-40% and Makes Your Home More Comfortable Year-Round',
    heroImage:
      'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'Most homes in America are under-insulated. The Department of Energy estimates that proper insulation can reduce heating and cooling costs by 20-40%, making it one of the most cost-effective home improvements available.',
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1504307651254-35680f356dfd?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Proper attic insulation is one of the most cost-effective ways to lower your energy bills.',
      },
      {
        type: 'stats',
        items: [
          { value: '20-40%', label: 'Energy Savings' },
          { value: '90%', label: 'Of Homes Under-Insulated' },
          { value: '1-2 Years', label: 'Typical Payback Period' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: "Modern injection foam insulation can be installed without removing drywall, making it possible to upgrade your entire home's insulation in a single day with minimal disruption.",
      },
      {
        type: 'quote',
        text: 'After insulating our walls and attic, our energy bill dropped by $150/month. The investment paid for itself in 18 months.',
        author: 'Greg & Diane F., St. Louis, MO',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free energy assessment today and find out how much you could save with proper insulation.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'doors',
    serviceName: 'Doors',
    headline:
      'First Impressions Matter: Why a New Front Door Is the Smartest Home Upgrade',
    subtitle:
      'Entry Door Replacement Delivers the Highest ROI of Any Home Improvement Project',
    heroImage:
      'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "According to Remodeling Magazine's Cost vs. Value report, a new steel entry door delivers a staggering 100%+ return on investment — the highest of any home improvement project. It's the first thing visitors see and the last thing they remember.",
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1513694203232-719a280e022f?auto=format&fit=crop&w=1200&q=80',
        caption:
          "A new entry door can dramatically improve your home's curb appeal and security.",
      },
      {
        type: 'stats',
        items: [
          { value: '100%+', label: 'ROI at Resale' },
          { value: '30%', label: 'Energy Loss Through Old Doors' },
          { value: '1 Day', label: 'Installation Time' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Modern entry doors offer superior insulation, security features, and design options. Fiberglass doors can mimic the look of real wood without the maintenance, while steel doors provide maximum security at an affordable price.',
      },
      {
        type: 'quote',
        text: 'Our new front door completely changed the look of our home. We get compliments from neighbors every week.',
        author: 'Angela R., Philadelphia, PA',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free door consultation today. Browse hundreds of styles and find the perfect entry door for your home.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'garage-doors',
    serviceName: 'Garage Doors',
    headline:
      "The Curb Appeal Secret: How a New Garage Door Transforms Your Home's Look",
    subtitle:
      "Garage Doors Account for 30% of Your Home's Front Facade — Make It Count",
    heroImage:
      'https://images.unsplash.com/photo-1558036117-15d82a90b9b1?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "Your garage door is one of the largest visual elements of your home's exterior. A new garage door can dramatically improve curb appeal while adding insulation, security, and smart home connectivity.",
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1605810230434-7631ac76ec81?auto=format&fit=crop&w=1200&q=80',
        caption:
          'A new garage door can completely transform the exterior look of your home.',
      },
      {
        type: 'stats',
        items: [
          { value: '94%', label: 'ROI at Resale' },
          { value: '30%', label: "Of Home's Front Facade" },
          { value: '1 Day', label: 'Installation Time' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: "Today's garage doors come with insulated panels, smart openers with app control, battery backup systems, and designs that complement any architectural style — from modern to craftsman to carriage house.",
      },
      {
        type: 'quote',
        text: "The new garage door made our house look 10 years newer. It was the single best curb appeal investment we've made.",
        author: 'Jeff & Tammy L., Raleigh, NC',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free design consultation and estimate today. See how a new garage door can transform your home.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'home-security',
    serviceName: 'Home Security',
    headline:
      'Why Smart Home Security Systems Are Becoming Essential for Every Homeowner',
    subtitle:
      'AI-Powered Cameras, Smart Locks, and 24/7 Monitoring Are More Affordable Than Ever',
    heroImage:
      'https://images.unsplash.com/photo-1558002038-1055907df827?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "A home burglary occurs every 25.7 seconds in America. But homes with security systems are 300% less likely to be targeted. With today's smart technology, comprehensive home security is more affordable and effective than ever.",
      },
      {
        type: 'image',
        src: 'https://cdn.magicpatterns.com/uploads/5Rf2g6s77D64RWVHLWz39v/Installing_a_home_security_camera.png',
        caption:
          'Modern smart home security systems can be monitored and controlled directly from your smartphone.',
      },
      {
        type: 'stats',
        items: [
          { value: '300%', label: 'Less Likely to Be Burglarized' },
          { value: '$17.99/mo', label: 'Starting Monitoring Cost' },
          { value: '20%', label: 'Insurance Discount' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: "Modern systems combine AI-powered cameras, smart locks, motion sensors, and 24/7 professional monitoring into a seamless package controlled from your smartphone. Many systems also qualify for homeowner's insurance discounts of 10-20%.",
      },
      {
        type: 'quote',
        text: 'Our security system caught a package thief on camera and the police arrested them within an hour. Worth every penny.',
        author: 'Michelle K., Scottsdale, AZ',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free home security assessment today. Many systems include free equipment and professional installation.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'pest-control',
    serviceName: 'Pest Control',
    headline:
      'The Hidden Pest Problem in Your Home — And Why Acting Now Saves Thousands',
    subtitle:
      "Termites Alone Cause $5 Billion in Damage Annually. Don't Let Your Home Be Next.",
    heroImage:
      'https://images.unsplash.com/photo-1632483485005-1f5f1f5f1f5f?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'Most pest damage happens silently, behind walls and under floors. By the time you see visible signs, thousands of dollars in damage may already be done. Regular professional pest control is the only way to catch problems early.',
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1584820927498-cafe2c1c969b?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Professional pest control technicians can identify and treat infestations before they cause major damage.',
      },
      {
        type: 'stats',
        items: [
          { value: '$5B', label: 'Annual Termite Damage' },
          { value: '1 in 5', label: 'Homes Have Pest Issues' },
          { value: '$50/mo', label: 'Average Prevention Cost' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Modern pest control uses targeted, eco-friendly treatments that are safe for families and pets while being highly effective against common pests. Quarterly service plans provide year-round protection at a fraction of the cost of damage repair.',
      },
      {
        type: 'quote',
        text: 'A routine inspection found termites in our garage wall. Early detection saved us from what could have been $10,000+ in structural damage.',
        author: 'Paul & Janet W., Tampa, FL',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free pest inspection today. Most companies offer same-day or next-day service.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'landscaping',
    serviceName: 'Landscaping',
    headline:
      'Professional Landscaping: The Investment That Pays You Back Every Day',
    subtitle:
      'Curb Appeal, Property Value, and Quality of Life — Why Landscaping Matters More Than You Think',
    heroImage:
      'https://images.unsplash.com/photo-1558904541-efa843a96f01?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "Professional landscaping can increase your home's value by 10-15% and reduce time on market by up to 6 weeks. But beyond the financial benefits, a well-maintained landscape improves your daily quality of life and reduces stress.",
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?auto=format&fit=crop&w=1200&q=80',
        caption:
          "Professional landscaping can increase your home's value by up to 15%.",
      },
      {
        type: 'stats',
        items: [
          { value: '10-15%', label: 'Home Value Increase' },
          { value: '6 Weeks', label: 'Faster Time to Sell' },
          { value: '100%+', label: 'ROI on Mature Trees' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'From weekly lawn maintenance to full landscape design and installation, professional landscapers handle everything from mowing and trimming to hardscaping, irrigation, and seasonal plantings.',
      },
      {
        type: 'quote',
        text: "Our landscaper transformed our bare backyard into an outdoor living space. It's like adding another room to our house.",
        author: 'Chris & Kelly N., Nashville, TN',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free landscape consultation today. A designer will assess your property and create a custom plan that fits your vision and budget.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'cleaning',
    serviceName: 'Cleaning',
    headline:
      'Why More Homeowners Are Hiring Professional Cleaners Than Ever Before',
    subtitle:
      'The Math Is Simple: Professional Cleaning Saves Time, Reduces Stress, and Costs Less Than You Think',
    heroImage:
      'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: "The average American spends 6 hours per week cleaning their home. At the median hourly wage, that's over $10,000 worth of time per year. Professional cleaning services start at a fraction of that cost while delivering superior results.",
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1581578731548-c64695cc6952?auto=format&fit=crop&w=1200&q=80',
        caption:
          'Professional cleaning services use specialized equipment to achieve a deeper clean.',
      },
      {
        type: 'stats',
        items: [
          { value: '6 Hours', label: 'Weekly Cleaning Time' },
          { value: '$120-200', label: 'Average Cleaning Cost' },
          { value: '99.9%', label: 'Germ Elimination' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Professional cleaners use commercial-grade equipment and EPA-registered products that eliminate 99.9% of germs and allergens. They follow systematic cleaning protocols that ensure consistent, thorough results every visit.',
      },
      {
        type: 'quote',
        text: 'Hiring a cleaning service was the best decision we made this year. We get our weekends back and the house has never been cleaner.',
        author: 'Samantha & Drew H., Seattle, WA',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free cleaning estimate today. Most services offer customizable plans that fit your schedule and budget.',
      },
      { type: 'cta' },
    ],
  },
  {
    serviceSlug: 'general',
    serviceName: 'General Repair',
    headline:
      'Stop Putting Off Home Repairs: Why a Professional Handyman Saves You Time and Money',
    subtitle: 'From Leaky Faucets to Deck Repairs, One Call Handles It All',
    heroImage:
      'https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=1200&q=80',
    sections: [
      {
        type: 'paragraph',
        text: 'Every homeowner has a to-do list of small repairs that never seems to get shorter. A professional handyman can tackle that entire list in a single visit, saving you weekends of frustration and ensuring every job is done right.',
      },
      {
        type: 'image',
        src: 'https://images.unsplash.com/photo-1504148455328-c376907d081c?auto=format&fit=crop&w=1200&q=80',
        caption:
          'A qualified handyman can tackle multiple home repair projects in a single visit.',
      },
      {
        type: 'stats',
        items: [
          { value: '10+ Jobs', label: 'Completed Per Visit' },
          { value: '$75-125/hr', label: 'Average Handyman Rate' },
          { value: '1 Day', label: 'Most Projects Complete' },
        ],
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Professional handymen are multi-skilled tradespeople who handle everything from drywall repair and painting to plumbing fixes, electrical outlets, deck maintenance, and furniture assembly. They come equipped with professional tools and materials.',
      },
      {
        type: 'quote',
        text: 'I had a list of 12 things that had been bugging me for months. The handyman knocked them all out in one afternoon. Should have called sooner!',
        author: 'Dan R., Indianapolis, IN',
      },
      { type: 'cta' },
      {
        type: 'paragraph',
        text: 'Get a free project consultation today. Most handyman services offer upfront, flat-rate pricing with no surprises.',
      },
      { type: 'cta' },
    ],
  },
]
